import {
  CustomerVehicleReduced,
  CustomerVehiclesFilterParamsReduced,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { useVehiclesFilterReduced } from '@/core/domain/people/Customers/Customers.hooks';
import { useEffect, useState } from 'react';
import { Key } from 'antd/es/table/interface';
import { ModalProps } from '@/types/ModalProps';
import { Button, Col, Modal, Row, Typography } from 'antd';
import VehiclesFiltersTableModal from './VehiclesFiltersTableModal';
import VehiclesTableModal from './VehiclesTableModal';
import { ColumnsType } from '../InfiniteTable';
import { CheckOutlined } from '@ant-design/icons';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

interface SelectedVehicleProps extends ModalProps {
  finishCallback?: (vehicle?: CustomerVehicleReduced[]) => void;
  selectedVehicle?: CustomerVehicleReduced[];
  noSelection?: boolean;
  onVehicleSelect?: (vehicle: CustomerVehicleReduced) => void;
}

const VehiclesSelectedModal: React.FC<SelectedVehicleProps> = ({
  visible,
  selectedVehicle = [],
  finishCallback,
  handleVisible,
  noSelection,
  onVehicleSelect,
}) => {
  const [queryParams, setQueryParams] =
    useState<CustomerVehiclesFilterParamsReduced>({
      active: true,
    });

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useVehiclesFilterReduced(queryParams, visible);

  const vehicles = flatInfiniteData(data);

  const [selectedVehiclesKeys, setSelectedVehiclesKeys] = useState<Key[]>([]);

  useEffect(() => {
    if (visible) {
      setSelectedVehiclesKeys(selectedVehicle.map((vehicle) => vehicle.id));
    }
  }, [visible]);

  const getSelectedVehicles = (): CustomerVehicleReduced[] => {
    const result = vehicles.filter((vehicle) => {
      return selectedVehiclesKeys.includes(vehicle.id);
    });

    return result;
  };

  const handleCancel = (): void => {
    setQueryParams({});
    setSelectedVehiclesKeys([]);

    handleVisible();
  };

  const handleOk = (): void => {
    const vehicles = getSelectedVehicles();
    if (finishCallback) {
      finishCallback(vehicles);
    }

    handleCancel();
  };

  const columns: ColumnsType<CustomerVehicleReduced> = [
    {
      dataIndex: 'licensePlate',
      title: 'Placa',
      width: 100,
    },
    {
      dataIndex: 'name',
      title: 'Veículos',
      render: (_, row) => row.modelName,
      ellipsis: true,
      width: 250,
    },
    {
      dataIndex: 'chassi',
      title: 'Chassi',
      ellipsis: true,
      width: 200,
    },
    onVehicleSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onVehicleSelect(row)}
              icon={<CheckOutlined />}
            />
          ),
          width: '20%',
        }
      : {},
  ];

  const handleVehiclesSelect = (selectedRowKeys: Key[]) => {
    setSelectedVehiclesKeys(selectedRowKeys);
  };

  return (
    <Modal
      title="Adicionar Veículo"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={900}
    >
      <Row gutter={16}>
        <Col flex="1">
          <Typography.Title style={{ marginBottom: '24px' }} level={4}>
            Busque os veículos
          </Typography.Title>

          <VehiclesFiltersTableModal
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />

          <VehiclesTableModal
            noSelection={noSelection}
            isFetching={isFetching}
            columns={columns}
            vehicle={vehicles}
            selectedVehiclesKeys={selectedVehiclesKeys}
            handleVehiclesSelect={handleVehiclesSelect}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default VehiclesSelectedModal;
